import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { SpxUpdatePendingActions, spxUpdateCheckActions } from '@softpak/components/spx-update';
import { mergeMap } from 'rxjs';

import { StatsUpdateLogActions } from './stats-update-log.actions';
import { spxChannelActions } from '@softpak/components/spx-channel-selection';
import { StatsUpdateLogQueryService } from 'src/app/shared/services/stats-update-log-query.service';

@Injectable()
export class StatsUpdateLogTrackEffects {
    private readonly actions$ = inject(Actions);
    private readonly statsUpdateLogQueryService = inject(StatsUpdateLogQueryService);
    private currentUpdateCheckType: string = 'BACKGROUND';

    onUpdateInitialize$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.initialize),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_CHECK_INITIALIZE', 'STARTED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onUpdateRunCheck$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.runCheck),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_CHECK_RUN', 'STARTED', {
                updateCheckType: this.resolveUpdateCheckType(!!action.forceWaitForUpdate),
                details: {
                    updateCheckType: this.currentUpdateCheckType,
                    forceWaitForUpdate: !!action.forceWaitForUpdate,
                },
            }),
        })])
    ));

    onUpdateSyncStarted$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.syncStarted),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_SYNC', 'STARTED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onUpdateReloadStarted$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.reloadStarted),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_RELOAD', 'STARTED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onUpdateReady$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.anUpdateIsReady),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_READY', 'SUCCEEDED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onNoUpdateFound$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.noUpdateWasFound),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_NOT_FOUND', 'SUCCEEDED', {
                details: {
                    startUpdateAgainAfterTimeout: !!action.startUpdateAgainAfterTimeout,
                },
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onUpdateCheckFailed$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.checkFailed),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_CHECK', 'FAILED', {
                details: {
                    startUpdateAgainAfterTimeout: !!action.startUpdateAgainAfterTimeout,
                },
                error: action.errorReason,
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onUpdateNotAvailableOnWeb$ = createEffect(() => this.actions$.pipe(
        ofType(spxUpdateCheckActions.notAvailableOnWeb),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_CHECK', 'SKIPPED', {
                details: 'not available on web',
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onPendingAccept$ = createEffect(() => this.actions$.pipe(
        ofType(SpxUpdatePendingActions.acceptUpdate),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_PENDING_ACCEPT', 'STARTED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onPendingDownloaded$ = createEffect(() => this.actions$.pipe(
        ofType(SpxUpdatePendingActions.hasBeenDownloaded),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_PENDING_DOWNLOADED', 'SUCCEEDED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onPendingInstalled$ = createEffect(() => this.actions$.pipe(
        ofType(SpxUpdatePendingActions.hasBeenInstalled),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_PENDING_INSTALLED', 'SUCCEEDED', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onPendingPostpone$ = createEffect(() => this.actions$.pipe(
        ofType(SpxUpdatePendingActions.postpone),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_PENDING_POSTPONED', 'INFO', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onPendingPostponeExpired$ = createEffect(() => this.actions$.pipe(
        ofType(SpxUpdatePendingActions.postponeExpired),
        mergeMap(() => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('UPDATE_PENDING_POSTPONE_EXPIRED', 'INFO', {
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onChannelChoose$ = createEffect(() => this.actions$.pipe(
        ofType(spxChannelActions.choose),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('CHANNEL_SELECT', 'STARTED', {
                customer: action.channel?.brand,
                channelType: action.channelType,
                details: {
                    serviceURI: action.channel?.serviceURI,
                },
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onChannelChooseSucceeded$ = createEffect(() => this.actions$.pipe(
        ofType(spxChannelActions.chooseSucceeded),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('CHANNEL_SELECT', 'SUCCEEDED', {
                customer: action.channel?.brand,
                channelType: action.channelType,
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    onChannelChooseFailed$ = createEffect(() => this.actions$.pipe(
        ofType(spxChannelActions.chooseFailed),
        mergeMap(action => [StatsUpdateLogActions.queue({
            query: this.statsUpdateLogQueryService.createUpdateLogQuery('CHANNEL_SELECT', 'FAILED', {
                customer: action.channel?.brand,
                channelType: action.channelType,
                updateCheckType: this.currentUpdateCheckType,
            }),
        })])
    ));

    private resolveUpdateCheckType(forceWaitForUpdate: boolean): string {
        this.currentUpdateCheckType = forceWaitForUpdate ? 'UPDATE' : 'BACKGROUND';
        return this.currentUpdateCheckType;
    }

}
